import {
  type Beneficiary,
  BeneUserRole,
  type PrescriptionDrug,
  UserRole,
} from "../../../@types";
import { hasGlobalSession } from "../../../helpers/loginHelpers";
import { getYearPartInfo } from "../../../helpers/yearFlagHelpers";
import { routes } from "../../routes";
import { Ga4EventDimensionsConfig, ga4EventDimensionValues } from "./constants";
import {
  type Ga4EventDimensions,
  Ga4EventDimension,
  type TealiumEventProps,
  Ga4Event,
  LinkType,
  type AnalyticsKey,
} from "./types";
import type { ParentComponentType } from "./eventTypes";
import {
  getHashPathName,
  getIsBeneLandingOrCallbackRoute,
} from "../../../helpers/routeHelpers";
import { getEnvironment } from "../../../helpers/urlHelpers";
import {
  PARENT_COMPONENT_HEADING_ATTRIBUTE,
  PARENT_COMPONENT_TYPE_ATTRIBUTE,
} from "../../../components/AnalyticsListeners";
import type { AppState } from "../../../@types/context";
/**
 * Constructs the proper GA4 object depending on state, analytics settings passed in, and typing in Ga4EventDimensionsConfig
 * @param settings
 * @param state
 */
export function createGa4Event({
  settings,
  state,
}: {
  settings: Ga4EventDimensions;
  state: AppState;
}): Ga4EventDimensions {
  const { event_name } = settings;
  const {
    beneficiary,
    csr,
    cwCoverage,
    language,
    lis,
    planType,
    prescriptions,
    cachedFlags: flags,
  } = state;
  const { isOutsideOpenEnrollment } = getYearPartInfo(flags);

  const logged_in = hasGlobalSession(state);
  /** for analytics, this should be true for any time outside of OOE */
  const open_enrollment = !isOutsideOpenEnrollment;

  const isCsr = csr || getHashPathName() === routes.csrLanding;
  const isBene = beneficiary || getIsBeneLandingOrCallbackRoute();

  const dimensionValueMapping: Partial<
    Record<AnalyticsKey, Ga4EventDimensions[AnalyticsKey]>
  > = {
    [Ga4EventDimension.CONTENT_LANGUAGE]: language,
    [Ga4EventDimension.LOGGED_IN]: logged_in,
    [Ga4EventDimension.MCT_COVERAGE_WIZARD]: !!cwCoverage,
    [Ga4EventDimension.MCT_INSURANCE_PROVIDER]: settings.mct_insurance_provider,
    [Ga4EventDimension.MCT_INSURANCE_TYPE]: settings.mct_insurance_type,
    [Ga4EventDimension.MCT_LIS]: lis,
    [Ga4EventDimension.MCT_MEDIGAP]: !!settings.mct_medigap,
    [Ga4EventDimension.MCT_PHARMACY_TYPE]:
      settings.mct_pharmacy_type === undefined
        ? state.pharmacyType
        : settings.mct_pharmacy_type,
    [Ga4EventDimension.MCT_PLAN_TYPE]: settings.mct_plan_type || planType,
    [Ga4EventDimension.MCT_VIEWED_DRUGS]: prescriptions.length > 0,
    [Ga4EventDimension.OPEN_ENROLLMENT]: open_enrollment,
    [Ga4EventDimension.ROLE]: (() => {
      let role = UserRole.AnonBeneficiary;
      if (isCsr) {
        role = UserRole.CSR;
      } else if (isBene) {
        role = UserRole.AuthBeneficiary;
      }
      return role;
    })(),
    [Ga4EventDimension.SITE_ENVIRONMENT]: getEnvironment(),
    [Ga4EventDimension.SITE_DOMAIN]: window.location.hostname,
    [Ga4EventDimension.CSR_ID]: beneficiary?.csr_id,
    [Ga4EventDimension.BENEFICIARY_KEY]: beneficiary?.meta_data.beneficiary_key,
  };
  const eventDimensions =
    Ga4EventDimensionsConfig[event_name as Ga4Event] || [];

  let dimensions: Ga4EventDimensions = { event_name };

  eventDimensions.forEach((k: AnalyticsKey) => {
    const mappedValue = dimensionValueMapping[k];
    const settingsValue = settings[k];
    if (mappedValue !== undefined) {
      dimensions = {
        ...dimensions,
        [k]: mappedValue,
      };
    } else if (settingsValue !== undefined) {
      if (typeof settingsValue === "object" && !Array.isArray(settingsValue)) {
        for (const [key, value] of Object.entries(settingsValue)) {
          if (
            ga4EventDimensionValues.find(dimension => dimension === key) &&
            value !== undefined
          ) {
            dimensions = {
              ...dimensions,
              [k]: value,
            };
          }
        }
      } else {
        dimensions = {
          ...dimensions,
          [k]: settingsValue,
        };
      }
    }
  });
  return dimensions;
}

/**
 * Constructs and sends the proper GA4 object depending on state, analytics settings passed in, and typing in Ga4EventDimensionsConfig
 * @param settings
 * @param state
 */
export const sendGa4Event = ({
  settings,
  state,
}: {
  settings: Ga4EventDimensions;
  state: AppState;
}): void => {
  if (!window.utag?.link) {
    return;
  }
  const dimensions = createGa4Event({ settings, state });
  window.utag.link(dimensions as unknown as Record<string, unknown>);
};

/**
 * Constructs the proper UA object
 */
export const createTealiumEvent = ({
  event_action,
  event_label,
  other_props = {},
  event_category = "MCT",
}: TealiumEventProps) => ({
  event_category,
  event_action,
  event_label,
  event_noninteraction: 0,
  ...other_props,
});

export function sendDrugsManagedAnalyticsEvent(
  prescriptions: PrescriptionDrug[]
) {
  if (!window.utag?.link) {
    return;
  }
  const drugNames = prescriptions.map(p => p.name).join("|");
  const drugTypes = prescriptions
    .map(p => (p.is_generic ? "generic" : "brand"))
    .join("|");

  window.utag.link({
    event_name: "mct_plan_finder_drugs_managed",
    event_category: "MCT",
    event_action: "Find Plans - Confirm Drugs",
    event_label: `${prescriptions.length}|${drugNames}`,
    drug_name: drugNames,
    drug_count: prescriptions.length,
    drug_type: drugTypes,
  });
}

export function sendPrintAnalyticsEvent() {
  if (!window.utag?.link) {
    return;
  }
  window.utag.link({
    event_name: Ga4Event.PRINT,
    link_type: LinkType.LINK_OTHER,
  });
}

/**
 * helper to add parent component props with type-safety
 * @example
 *  <MedicareGovAnchor
      urlKey="prescriptionPaymentPlan"
      {...addParentComponentProps(
        t(m3pModalHeadingTranslationKey),
        "modal"
      )}
    >
*/
export const addParentComponentProps = (
  parent_component_heading: string,
  parent_component_type: ParentComponentType
) => ({
  [PARENT_COMPONENT_HEADING_ATTRIBUTE]: parent_component_heading,
  [PARENT_COMPONENT_TYPE_ATTRIBUTE]: parent_component_type,
});

export const getProxyUser = (beneficiary?: Beneficiary) => {
  if (
    beneficiary &&
    (beneficiary.meta_data.role === BeneUserRole.Full ||
      beneficiary.meta_data.role === BeneUserRole.Limited)
  ) {
    return beneficiary.meta_data.is_enrollment_allowed === false
      ? UserRole.ProxyCaregiverEnrollmentNotAllowed
      : UserRole.ProxyCaregiverEnrollmentAllowed;
  }
  return UserRole.AuthBeneficiary;
};
